import { useAsyncData } from '#imports'
import { type MaybeRefOrGetter, computed, toRef } from 'vue'

import type { AlgoliaProduct } from '@backmarket/http-api/src/api-specs-search-reco/search/searchAlgolia'
import { useAlgoliaSearchOptions } from '@backmarket/nuxt-layer-algolia/useAlgoliaSearchOptions'
import { useHttpFetch } from '@backmarket/nuxt-module-http-v2/useHttpFetch'
import algoliasearch from 'algoliasearch'

import { algoliaHitToVariant } from '../../helpers/recommendationHelper'

import type {
  AlgoliaRecommendationsModel,
  AlgoliaRecommendationsObjectIds,
  AlgoliaRecommendationsOptions,
} from './useAlgoliaRecommendations.types'
import { sortAndMergeRecommendations } from './useAlgoliaRecommendations.utils'

export function useAlgoliaRecommendations(
  objectIds: MaybeRefOrGetter<AlgoliaRecommendationsObjectIds>,
  model: AlgoliaRecommendationsModel,
  options?: MaybeRefOrGetter<AlgoliaRecommendationsOptions | undefined>,
) {
  const objectIdsRef = toRef(objectIds)
  const optionsRef = toRef(options)

  const $httpFetch = useHttpFetch()

  const { data: configurationData } = useAsyncData(
    'algoliaRecommendationsConfig',
    () => $httpFetch('/bm/recommendation/configuration'),
    { dedupe: 'defer' },
  )

  const queryKey = computed(() =>
    JSON.stringify({
      indexName: configurationData.value?.indexName,
      objectIds: objectIdsRef.value,
      model,
      ...optionsRef.value,
    }),
  )

  return useAsyncData(
    queryKey.value,
    async () => {
      if (
        !objectIdsRef.value ||
        objectIdsRef.value.length === 0 ||
        !configurationData.value?.indexName
      )
        return null

      const algoliaSearchOption = useAlgoliaSearchOptions('recommendations')
      const algoliaClient = algoliasearch(
        'dummy_app_id',
        'dummy_key',
        algoliaSearchOption,
      )

      const { indexName } = configurationData.value

      const requests = objectIdsRef.value.map((id) => ({
        indexName,
        objectID: id,
        model,
        ...optionsRef.value,
      }))

      return algoliaClient.getRecommendations<AlgoliaProduct>(requests)
    },
    {
      lazy: true,
      server: false,
      dedupe: 'defer',
      default: () => [],
      deep: false,
      watch: [queryKey],
      transform: (response) => {
        if (!response) {
          return []
        }

        const { results } = response
        const allHits = results.map((result) => result.hits)
        const mergedHits = sortAndMergeRecommendations<AlgoliaProduct>(
          allHits,
          objectIdsRef.value ?? [],
        )

        const limitedHits = optionsRef.value?.maxRecommendations
          ? mergedHits.slice(0, optionsRef.value?.maxRecommendations)
          : mergedHits

        return limitedHits.map((hit) => algoliaHitToVariant(hit))
      },
    },
  )
}
